import React, { useState, useEffect } from 'react';
import './styles/DeliveryOptions.css';

interface DeliveryOptionsProps {
  onDeliverySelect?: (deliveryType: 'immediate' | 'schedule' | null) => void;
  // Optional detailed callback to propagate schedule selections upstream without breaking existing prop
  onDeliveryDetailsChange?: (details: {
    deliveryType: 'immediate' | 'schedule';
    scheduledDeliveryDate?: string;
    scheduledDeliveryTime?: string;
  }) => void;
}

const DeliveryOptions: React.FC<DeliveryOptionsProps> = ({ onDeliverySelect, onDeliveryDetailsChange }) => {
  const [selected, setSelected] = useState<'immediate' | 'schedule'>('immediate');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');

  const isStoreOpen = () => {
    const now = new Date();
    const hours = now.getHours();
    return hours >= 8 && hours < 20; // 8 AM to 8 PM
  };

  useEffect(() => {
    // If store is closed, auto-select schedule
    if (!isStoreOpen() && selected === 'immediate') {
      setSelected('schedule');
      onDeliverySelect?.('schedule');
    }
  }, []);

  useEffect(() => {
    if (selected === 'immediate') {
      onDeliveryDetailsChange?.({ deliveryType: 'immediate' });
    }
    else if (selected === 'schedule' && selectedDate && selectedTime) {
      onDeliveryDetailsChange?.({ deliveryType: 'schedule', scheduledDeliveryDate: selectedDate, scheduledDeliveryTime: selectedTime });
    }
    else if (selected === 'schedule') {
      // Incomplete schedule, still let parent know current partial selection
      onDeliveryDetailsChange?.({ deliveryType: 'schedule', scheduledDeliveryDate: selectedDate || undefined, scheduledDeliveryTime: selectedTime || undefined });
    }
  }, [selected, selectedDate, selectedTime, onDeliveryDetailsChange]);

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        display: date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    return dates;
  };
  const timeSlots = [
    { value: '09:00-11:00', display: '9:00 AM - 11:00 AM' },
    { value: '11:00-13:00', display: '11:00 AM - 1:00 PM' },
    { value: '13:00-15:00', display: '1:00 PM - 3:00 PM' },
    { value: '15:00-17:00', display: '3:00 PM - 5:00 PM' },
    { value: '17:00-19:00', display: '5:00 PM - 7:00 PM' }
  ];

  return (
    <div className="card delivery-options-card">
      <div className="card-header">
        <div className="card-title">
          <img className="title-icon" src="/icon/delivery.png" alt="Delivery" /> Delivery Options
        </div>
      </div>
      <div className="card-body">
        {!isStoreOpen() && (
          <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#856404' }}>
            <strong>Note:</strong> The store is currently closed. Your order will be delivered as soon as the store opens at 8:00 AM IST.
          </div>
        )}
        <div className="delivery-options">
          {isStoreOpen() && (
            <button
              type="button"
              className={`d-option ${selected === 'immediate' ? 'active' : ''}`}
              onClick={() => {
                setSelected('immediate');
                onDeliverySelect?.('immediate');
              }}
            >
              <span className={`radio ${selected === 'immediate' ? 'checked' : ''}`}/>
              <div className="d-content">
                <div className="d-title">Immediate Delivery</div>
                <div className="d-desc">Available Tomorrow from 9:00 AM IST</div>
                <div className="d-desc">Service hours: 9:00 AM - 8:00 PM IST</div>
              </div>
            </button>
          )}
          <button
            type="button"
            className={`d-option ${selected === 'schedule' ? 'active' : ''}`}
            onClick={() => {
              setSelected('schedule');
              onDeliverySelect?.('schedule');
            }}
          >
            <span className={`radio ${selected === 'schedule' ? 'checked' : ''}`}/>
            <div className="d-content">
              <div className="d-title">Schedule Delivery</div>
              <div className="d-desc">Choose your preferred date and time</div>
            </div>
          </button>
        </div>

        {selected === 'schedule' && (!selectedDate || !selectedTime) && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', color: '#856404', fontSize: '14px', textAlign: 'center' }}>
            <strong>Please scroll down to select your preferred delivery date and time slot.</strong>
          </div>
        )}

        {selected === 'schedule' && (
          <div className="schedule-options" style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div className="schedule-section">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                Select Date:
              </label>
              <div className="date-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', marginBottom: '16px' }}>
                {getAvailableDates().map((date) => (
                  <button
                    key={date.value}
                    type="button"
                    className={`date-option ${selectedDate === date.value ? 'selected' : ''}`}
                    onClick={() => setSelectedDate(date.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: selectedDate === date.value ? '#007bff' : 'white',
                      color: selectedDate === date.value ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'center'
                    }}
                  >
                    {date.display}
                  </button>
                ))}
              </div>
            </div>

            <div className="schedule-section">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                Select Time Slot:
              </label>
              <div className="time-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                {timeSlots.map((time) => (
                  <button
                    key={time.value}
                    type="button"
                    className={`time-option ${selectedTime === time.value ? 'selected' : ''}`}
                    onClick={() => setSelectedTime(time.value)}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: selectedTime === time.value ? '#007bff' : 'white',
                      color: selectedTime === time.value ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textAlign: 'center'
                    }}
                  >
                    {time.display}
                  </button>
                ))}
              </div>
            </div>

            {selectedDate && selectedTime && (
              <div className="selected-schedule" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e8f5e8', borderRadius: '6px', border: '1px solid #28a745' }}>
                <div style={{ color: '#155724', fontSize: '14px', fontWeight: '500' }}>
                  ✓ Scheduled for: {getAvailableDates().find(d => d.value === selectedDate)?.display} - {timeSlots.find(t => t.value === selectedTime)?.display}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryOptions;


